import React, { ChangeEvent, useState } from 'react';

import { InfoOutline } from '@styled-icons/evaicons-outline/InfoOutline';
import {
  IconCircleArrowLeft,
  IconInfoCircle,
  IconSearch,
  IconWand,
  IconX,
} from '@tabler/icons-react';
import ValuationCalculator from 'components/ValuationCalculator/ValuationCalculator';
import { NavLink } from 'react-router-dom';
import {
  Accordion,
  Button,
  ButtonWithIcon,
  ModalNotStyled,
  Spinner,
  UiInput,
  Dialog,
  useDialog,
} from 'ui-kit';
import { downloadElementAsPDF } from 'utils/pdfUtils';

import { useCREModal } from '../hooks/useCREModal';
import { AIInsights } from './CREModal/AIInsights';
import { CREDetails } from './CREModal/CREDetails';
import { GalleryMapView } from './CREModal/GalleryMapView';
import { PropertyTaxes } from './CREModal/PropertyTaxes';
import { Transportation } from './CREModal/Transportation';

interface Props {
  handleCloseCREModal: () => void;
  creModalLoanId: string;
  loansData?: any;
  role?: string;
  isPortfolio?: boolean;
}

export function CreDetailsModal({
  creModalLoanId,
  handleCloseCREModal,
  loansData,
  role = 'SELLER',
  isPortfolio = true,
}: Props) {
  const confirmCloseDialog = useDialog();

  const [pdfCloneReady, setPdfCloneReady] = useState(false);

  const { data, searchValue, selectedLoanId, setSelectedLoanId, setSearchValue } = useCREModal({
    creModalLoanId,
    role,
  });

  const CREAccordionClasses = 'h-[36px] px-4 bg-gray-950 text-white !text-[12px] uppercase';

  const handleDownloadPdf = async () => {
    setPdfCloneReady(true);

    await new Promise((resolve) => setTimeout(resolve, 500));

    const container = document.getElementById('cre-download-section__pdf');
    if (!container) return;

    const images = Array.from(container.querySelectorAll('img'));
    await Promise.all(
      images.map((img) =>
        img.complete ? Promise.resolve() : new Promise((res) => (img.onload = res)),
      ),
    );

    await downloadElementAsPDF('cre-download-section__pdf', `CRE_Details_${creModalLoanId}`, 10);

    setPdfCloneReady(false);
  };

  const renderSections = (pdfMode = false) => {
    // const currentLoan = data?.find((i) => i.account_id === selectedLoanId);
    const defaultLoanAmount = Number(100000); // TODO add after BE integration
    const defaultPaymentsPerYear = 12;

    const sections = [
      {
        key: 'gallery',
        label: <div>gallery & map view</div>,
        content: <GalleryMapView />,
      },
      {
        key: 'details',
        label: <div>details</div>,
        content: <CREDetails />,
      },
      {
        key: 'valuation',
        label: <div>valuation calculator</div>,
        content: (
          <ValuationCalculator
            defaultLoanAmount={defaultLoanAmount}
            defaultPaymentsPerYear={defaultPaymentsPerYear}
          />
        ),
      },
      {
        key: 'taxes',
        label: <div>property taxes</div>,
        content: <PropertyTaxes />,
      },
      {
        key: 'transport',
        label: <div>transporation</div>,
        content: <Transportation />,
      },
    ];

    return sections.flatMap(({ key, label, content }, idx) => {
      const acc = (
        <Accordion
          key={`section-${key}`}
          label={label}
          hasBorder={false}
          labelClasses={CREAccordionClasses}
          containerClasses="!p-0"
          initialIsOpen={pdfMode || undefined}
        >
          {content}
        </Accordion>
      );

      // keep your PDF page-break behavior
      if (pdfMode) {
        return [<div key={`break-${key}-${idx}`} className="page-break" />, acc];
      }
      return [acc];
    });
  };

  return (
    <>
      <ModalNotStyled
        initialOpen
        fullscreen
        className="flex items-start justify-center p-4"
        handleClose={handleCloseCREModal}
      >
        {({ closeModal }) => {
          const handleRequestClose = () => confirmCloseDialog.openDialog();

          return (
            <div className="relative h-full w-full overflow-y-auto rounded-[8px] border border-pink-500 bg-slate-950 px-6 pb-2 text-[14px] text-slate-400">
              <div className="sticky top-0 z-10 flex items-center justify-between bg-slate-950 pt-2">
                <h2 className="text-xl text-white">CRE Details</h2>
                <IconX onClick={handleRequestClose} className="h-5 w-5 cursor-pointer text-white" />
              </div>

              <div className="h-full w-full bg-slate-950 p-[20px] px-[5%]">
                <ButtonWithIcon
                  label="Back to Loan Level Data"
                  icon={<IconCircleArrowLeft size={20} />}
                  iconPosition="left"
                  type="link"
                  className="mb-[16px] items-center border-none !px-0 text-[14px]"
                  onClick={handleRequestClose}
                  iconClass="w-5 h-5"
                />

                <div className="mb mb-[16px] flex items-center justify-between">
                  <p className="text-[18px] font-medium leading-[24px] text-white">
                    cre-loan-table-id
                  </p>
                  <div className="flex items-center gap-3">
                    <Button
                      label="Download PDF"
                      className="!h-[26px] !min-h-[26px]"
                      type="secondary"
                      size="medium"
                      onClick={handleDownloadPdf}
                      disabled={pdfCloneReady}
                    >
                      {pdfCloneReady ? (
                        <>
                          Generating PDF
                          <Spinner size={16} color="#E028DD" loading={true} className="ml-2" />
                        </>
                      ) : (
                        'Download PDF'
                      )}
                    </Button>

                    {role === 'SELLER' && isPortfolio && (
                      <NavLink to="/portfolio/pool-manager">
                        <Button
                          label="Create Offering"
                          className="!h-[26px] !min-h-[26px]"
                          size="medium"
                        >
                          Create Offering
                        </Button>
                      </NavLink>
                    )}
                  </div>
                </div>

                <div id="cre-download-section">
                  <Accordion
                    label={
                      <div className="flex items-center gap-2">
                        <IconWand size="20px" />
                        <p>A.I. Generated insights - General</p>
                        <div className="flex cursor-pointer items-center gap-1 text-brand-400">
                          <IconInfoCircle size="14px" />
                          <p>NOTE</p>
                        </div>
                      </div>
                    }
                    hasBorder={false}
                    labelClasses={CREAccordionClasses}
                    containerClasses="!p-0"
                  >
                    <AIInsights />
                  </Accordion>

                  {role === 'SELLER' && isPortfolio ? (
                    <div className="flex w-full items-start gap-2">
                      <div className="h-[70vh] w-[262px] rounded-[4px] border border-gray-900 p-2">
                        <div className="flex h-[7%] w-[100%] items-center justify-between gap-1">
                          <span className="w-[50%]">{data.length} Loans</span>
                          <UiInput
                            size="small"
                            value={searchValue}
                            placeholder="Search"
                            variant="gray"
                            Icon={IconSearch}
                            iconPosition="left"
                            iconClasses="!stroke-pink-400"
                            onChange={(e: ChangeEvent<HTMLInputElement>) =>
                              setSearchValue(e.target.value)
                            }
                          />
                        </div>
                        <div className="scrollbar-thin h-[90%] overflow-y-scroll" key={searchValue}>
                          {data?.map((item) => (
                            <div
                              key={item.id}
                              onClick={() =>
                                setSelectedLoanId((prev) =>
                                  selectedLoanId === item?.account_id ? prev : item?.account_id!,
                                )
                              }
                              className={`mb-2 cursor-pointer px-2 py-1 text-white ${
                                selectedLoanId === item?.account_id ? 'bg-brand-800' : ''
                              }`}
                            >
                              {item.account_id}
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="w-[80%]">{renderSections()}</div>
                    </div>
                  ) : (
                    renderSections()
                  )}
                </div>
              </div>

              <Dialog dialog={confirmCloseDialog}>
                <span
                  className="absolute right-4 top-4 inline-block cursor-pointer"
                  onClick={confirmCloseDialog.closeDialog}
                >
                  <IconX size={20} className="text-foreground-subtle" />
                </span>
                <div className="flex gap-6">
                  <div className="flex h-14 w-14 items-center justify-center rounded-lg bg-info-muted">
                    <InfoOutline size={24} className="text-black" />
                  </div>
                  <div className="flex w-[400px] flex-col text-left">
                    <h2 className="mb-2 text-xl">Are you sure you want to close?</h2>
                    <p className="text-base">
                      All unsaved changes will be lost if you close this modal.
                    </p>
                    <div className="mt-4 flex justify-end gap-3">
                      <Button
                        type="secondary"
                        onClick={confirmCloseDialog.closeDialog}
                        label="Cancel"
                      >
                        Cancel
                      </Button>
                      <Button
                        type="primary"
                        onClick={() => {
                          confirmCloseDialog.closeDialog();
                          closeModal();
                        }}
                        label="Yes, Close"
                      >
                        Yes, Close
                      </Button>
                    </div>
                  </div>
                </div>
              </Dialog>
            </div>
          );
        }}
      </ModalNotStyled>

      {pdfCloneReady && (
        <div
          id="cre-download-section__pdf"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '800px',
            zIndex: '-9999',
            background: '#0f172a',
            padding: '20px',
            height: 'auto',
            overflow: 'visible',
          }}
        >
          <Accordion
            label={
              <div className="flex items-center gap-2">
                <IconWand size="20" />
                <p>A.I. Generated insights - General</p>
              </div>
            }
            hasBorder={false}
            labelClasses={CREAccordionClasses}
            containerClasses="!p-0"
            initialIsOpen={true}
          >
            <AIInsights />
          </Accordion>

          {renderSections(true)}
        </div>
      )}
    </>
  );
}
